import { css } from '@emotion/react';
import { MenuItem, Pagination, Select, type SelectChangeEvent } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useCallback } from 'react';
import { colors } from '../../../theme/colors';
import { FMT } from '../../../util/formatter-service';
import { pluralizeWord } from '../../../util/stringUtils';

/** Default rows-per-page when the caller doesn't specify one. */
export const DEFAULT_PAGE_SIZE = 250;

/** Rows-per-page options exposed by the sourcing grid footer. */
export const PAGE_SIZE_OPTIONS: ReadonlyArray<number> = [50, 100, 250, 500];

/**
 * Constrain a requested page size to the supported option set.
 * Prevents the footer's <Select> from rendering a blank value when a caller
 * passes a size that isn't in PAGE_SIZE_OPTIONS.
 */
export function resolveInitialPageSize(requested: number | undefined): number {
  if (requested != null && PAGE_SIZE_OPTIONS.includes(requested)) {
    return requested;
  }
  return DEFAULT_PAGE_SIZE;
}

const containerCss = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 12px 10px;
  background-color: ${colors.secondary[10]};
  border-top: 1px solid ${colors.border.card};
`;

// Outer right-aligned group: total count + controls, with 28px gap between them
// (matches Figma node 2587:22235).
const resultsAndControlsCss = css`
  display: flex;
  align-items: center;
  gap: 28px;
`;

// Inner controls cluster: rows-per-page group + pagination, 8px apart
// (matches Figma node 2587:23301 / gap/s).
const controlsCss = css`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const totalCountCss = css`
  font-size: 14px;
  line-height: 1.43;
  letter-spacing: 0.17px;
  color: ${colors.text.main};
`;

const rowsPerPageGroupCss = css`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  line-height: 1.43;
  letter-spacing: 0.17px;
  color: ${colors.text.main};
`;

const pageSizeSelectSx = {
  height: 24,
  minHeight: '2rem',
  borderRadius: '4px',
  backgroundColor: colors.primary[0],
  '& .MuiSelect-select': {
    padding: '2px 24px 2px 8px',
    fontSize: 14,
    lineHeight: '20px',
    letterSpacing: '0.17px',
    color: colors.text.main,
    minHeight: 0,
  },
  '& .MuiSelect-icon': {
    fontSize: 16,
    right: 4,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: colors.border.card,
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: colors.border.card,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: colors.secondary[50],
    borderWidth: 1,
  },
};

const paginationSx = {
  '& .MuiPagination-ul': {
    gap: '8px',
    flexWrap: 'nowrap',
  },
  '& .MuiPaginationItem-root': {
    margin: 0,
    height: 24,
    minWidth: 24,
    padding: 0,
    fontSize: 14,
    lineHeight: '20px',
    letterSpacing: '0.17px',
    color: colors.text.main,
    borderRadius: '4px',
    '&.Mui-selected': {
      backgroundColor: colors.secondary[50],
      color: colors.primary[0],
      '&:hover': {
        backgroundColor: colors.secondary[60],
      },
    },
  },
  '& .MuiPaginationItem-icon': {
    fontSize: 20,
  },
};

interface SourcingGridPaginationFooterProps {
  /** Total number of rows across all pages. */
  readonly totalRowCount: number;
  /** Current page index, 0-based (AG Grid convention). */
  readonly currentPage: number;
  /** Total number of pages. */
  readonly totalPages: number;
  /** Currently selected rows per page. */
  readonly pageSize: number;
  /** Available rows-per-page options. */
  readonly pageSizeOptions: ReadonlyArray<number>;
  /** Invoked with the new 0-based page index. */
  readonly onPageChange: (page: number) => void;
  /** Invoked with the new page size; caller is expected to reset to page 1. */
  readonly onPageSizeChange: (pageSize: number) => void;
}

/**
 * Footer rendered below the sourcing grid when AG Grid pagination is enabled.
 * Replaces AG Grid's default panel with a layout matching the Figma design:
 *
 *                       [N results]   Rows per page [size ▼]   ‹ 1 2 … N ›
 *                       └── 28px ──┘└── 8px ──┘
 *
 * The whole cluster is right-aligned in the footer.
 */
export function SourcingGridPaginationFooter({
  totalRowCount,
  currentPage,
  totalPages,
  pageSize,
  pageSizeOptions,
  onPageChange,
  onPageSizeChange,
}: SourcingGridPaginationFooterProps) {
  const handlePageChange = useCallback(
    (_event: React.ChangeEvent<unknown>, page: number) => {
      // MUI Pagination is 1-indexed; AG Grid is 0-indexed.
      onPageChange(page - 1);
    },
    [onPageChange]
  );

  const handlePageSizeChange = useCallback(
    (event: SelectChangeEvent<number>) => {
      onPageSizeChange(Number(event.target.value));
    },
    [onPageSizeChange]
  );

  // MUI requires count >= 1 to render the control; clamp to ensure a stable layout
  // even when the grid has no results (control will be disabled by `count={1}`).
  const pageCount = Math.max(totalPages, 1);

  return (
    <div css={containerCss} data-testid='sourcing-grid-pagination-footer'>
      <div css={resultsAndControlsCss}>
        <span css={totalCountCss}>{formatTotalResults(totalRowCount)}</span>
        <div css={controlsCss}>
          <div css={rowsPerPageGroupCss}>
            <span>Rows per page</span>
            <Select
              value={pageSize}
              onChange={handlePageSizeChange}
              size='small'
              variant='outlined'
              IconComponent={KeyboardArrowDownIcon}
              sx={pageSizeSelectSx}
              inputProps={{ 'aria-label': 'Rows per page' }}
            >
              {pageSizeOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </div>
          <Pagination
            count={pageCount}
            page={Math.min(currentPage + 1, pageCount)}
            onChange={handlePageChange}
            siblingCount={1}
            boundaryCount={1}
            shape='rounded'
            size='small'
            // Hide the |< and >| controls per spec — only ‹ and › remain.
            showFirstButton={false}
            showLastButton={false}
            disabled={totalPages <= 1}
            sx={paginationSx}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * Pure formatter for the total-results label.
 * Singular for exactly one result; thousands-separated otherwise.
 */
export function formatTotalResults(count: number): string {
  return `${FMT.format('integer', count)} ${pluralizeWord('result', count)}`;
}
